import {useState, type ReactNode} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {Campo} from '@/components/campo'
import {DateField} from '@/components/date-field'
import {SectionCard} from '@/components/section-card'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'
import {EvidenceListItem, UploadTrigger} from '@/components/upload-trigger'
import {rutaDeDescarga, type Evidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {
  useGenerarFurat,
  useMarcarFuratManual,
  useMarcarInformeArl,
  useMarcarTerritorial,
  useTransicionarIncidente,
  type Incidente,
} from '@/hooks/sgsst/incidentes/use-incidentes'
import {
  useInvestigacion,
  useInvestigacionEvidencia,
  useSubirEvidenciaInvestigacion,
  type EvidenceKind,
} from '@/hooks/sgsst/incidentes/use-investigacion'
import {translateApiError} from '@/lib/errors'
import {formatDate, localToday} from '@/lib/risk'
import {cn} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

function FuratManualForm({businessId, incidenteId}: {businessId: string; incidenteId: string}) {
  const {t} = useTranslation()
  const [radicado, setRadicado] = useState('')
  const [fecha, setFecha] = useState(localToday())
  const [error, setError] = useState<string | null>(null)
  const marcar = useMarcarFuratManual(businessId, incidenteId)
  return (
    <div className="space-y-3" data-testid="furat-manual-form">
      <Campo label={t('incidentes.furat.radicadoLabel')} htmlFor="furat-radicado">
        <Input
          maxLength={tope('CODE')}
          id="furat-radicado"
          type="text"
          value={radicado}
          data-testid="furat-radicado"
          onChange={(e) => setRadicado(e.target.value)}
          placeholder={t('incidentes.furat.radicadoPlaceholder')}
          className="h-11 font-mono sm:h-9"
        />
      </Campo>
      <Campo label={t('incidentes.furat.radicadoFechaLabel')} htmlFor="furat-radicado-fecha">
        <Input
          id="furat-radicado-fecha"
          type="date"
          value={fecha}
          data-testid="furat-radicado-fecha"
          max={localToday()}
          onChange={(e) => setFecha(e.target.value)}
          className="h-11 font-mono tabular-nums sm:h-9"
        />
      </Campo>
      <Button
        size="sm"
        className="min-h-11 sm:min-h-0"
        data-testid="furat-manual-submit"
        disabled={!radicado.trim() || marcar.isPending}
        onClick={() => {
          setError(null)
          marcar.mutate(
            {radicado: radicado.trim(), radicado_fecha: fecha},
            {onError: (err) => setError(translateApiError(err))}
          )
        }}>
        {t('incidentes.furat.manualSubmit')}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

/** La misma transición que «Iniciar investigación», con la fecha adentro. Sin formulario HTML:
 * la validación es de la app, nunca del navegador. */
function InicioManualForm({
  businessId,
  incidenteId,
  eventoFecha,
}: {
  businessId: string
  incidenteId: string
  eventoFecha: string
}) {
  const {t} = useTranslation()
  // Vacío a propósito: una fecha legal jamás se pre-llena.
  const [fecha, setFecha] = useState('')
  const [error, setError] = useState<string | null>(null)
  const transicion = useTransicionarIncidente(businessId, incidenteId)
  const hoy = localToday()
  const fueraDeRango = !fecha || fecha < eventoFecha || fecha > hoy
  return (
    <div className="space-y-3" data-testid="inicio-manual-form">
      <DateField
        id="inicio-manual-fecha"
        label={t('incidentes.checklist.inicioManualFechaLabel')}
        value={fecha}
        min={eventoFecha}
        max={hoy}
        hint={t('incidentes.checklist.inicioManualHint')}
        onChange={setFecha}
      />
      <Hint testId="inicio-manual-aviso">{t('incidentes.checklist.inicioManualTip')}</Hint>
      <Button
        size="sm"
        className="min-h-11 sm:min-h-0"
        data-testid="inicio-manual-submit"
        disabled={fueraDeRango || transicion.isPending}
        onClick={() => {
          setError(null)
          transicion.mutate(
            {estado: 'en_investigacion', investigacion_started_fecha: fecha},
            {onError: (err) => setError(translateApiError(err))}
          )
        }}>
        {t('incidentes.checklist.inicioManualSubmit')}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

function ChecklistStep({
  done,
  siguiente,
  title,
  children,
  keepChildrenWhenDone = false,
  comprobante,
}: {
  done: boolean
  siguiente: boolean
  title: string
  children?: ReactNode
  // Una investigación «Hecha» solo empezó: su enlace es la única puerta al asistente.
  keepChildrenWhenDone?: boolean
  // Aparte de `children` porque sobrevive al «Hecho»: el comprobante solo existe tras radicar.
  comprobante?: ReactNode
}) {
  const {t} = useTranslation()
  return (
    <div
      className={cn(
        'surface space-y-2 border-l-[3px] p-4',
        done
          ? 'border-l-tone-green-fg'
          : siguiente
            ? 'border-l-tone-amber-fg shadow-[var(--shadow-card-hover)]'
            : 'border-l-border'
      )}
      data-siguiente={siguiente}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <span className="min-w-0 text-sm font-medium">{title}</span>
        {done ? (
          <Tag tone="green">{t('incidentes.checklist.hecho')}</Tag>
        ) : siguiente ? (
          <Tag tone="amber">{t('incidentes.checklist.siguiente')}</Tag>
        ) : (
          <Tag tone="neutral">{t('incidentes.checklist.pendiente')}</Tag>
        )}
      </div>
      {(!done || keepChildrenWhenDone) && children}
      {comprobante}
    </div>
  )
}

/** Cuelga de la investigación (`sgsst_evidence.investigacion_id`), así que también sale en la
 * evidencia de la ficha. Solo se pide el archivo: el radicado ya viene impreso. */
function ComprobanteBloque({
  businessId,
  incidenteId,
  investigacionId,
  kind,
  items,
}: {
  businessId: string
  incidenteId: string
  investigacionId: string
  kind: EvidenceKind
  items: Evidence[]
}) {
  const {t} = useTranslation()
  const {start, progress, error} = useSubirEvidenciaInvestigacion(businessId, incidenteId, investigacionId, kind)
  return (
    <div className="space-y-1.5 border-t border-border pt-2.5" data-testid={`comprobante-${kind}`}>
      <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        {t('incidentes.checklist.comprobante.titulo')}
      </p>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <EvidenceListItem
            key={item.id}
            item={item}
            ruta={rutaDeDescarga(businessId, item.id)}
            testId={`comprobante-${kind}-item`}
          />
        ))}
      </ul>
      {items.length === 0 && (
        <p className="text-xs text-muted-foreground">{t('incidentes.checklist.comprobante.vacio')}</p>
      )}
      <UploadTrigger
        label={t('incidentes.checklist.comprobante.adjuntar')}
        progress={progress}
        testId={`comprobante-${kind}`}
        onPick={start}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

/** Solo grave o mortal (art. 14): su sello arranca los relojes de concepto ARL y de Ministerio. */
function InformeArlStep({
  incidente,
  businessId,
  siguiente,
  comprobante,
}: {
  incidente: Incidente
  businessId: string
  siguiente: boolean
  comprobante?: ReactNode
}) {
  const {t} = useTranslation()
  const marcar = useMarcarInformeArl(businessId, incidente.id)
  const [error, setError] = useState<string | null>(null)
  const esGraveMortal = incidente.gravedad_reportada === 'grave' || incidente.gravedad_reportada === 'mortal'
  if (!esGraveMortal) return null
  const enviado = incidente.informe_arl_sent_at !== null
  return (
    <ChecklistStep
      done={enviado}
      siguiente={siguiente}
      title={t('incidentes.checklist.informeArlTitle')}
      comprobante={comprobante}>
      <Button
        size="sm"
        variant={siguiente ? 'default' : 'outline'}
        className="min-h-11 sm:min-h-0"
        data-testid="informe-arl-marcar"
        disabled={marcar.isPending}
        onClick={() => {
          setError(null)
          marcar.mutate(undefined, {onError: (err) => setError(translateApiError(err))})
        }}>
        {t('incidentes.checklist.informeArlMarcar')}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </ChecklistStep>
  )
}

export function Checklist({incidente, canOperate}: {incidente: Incidente; canOperate: boolean}) {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const generar = useGenerarFurat(businessId!, incidente.id)
  const territorial = useMarcarTerritorial(businessId!, incidente.id)
  const transicion = useTransicionarIncidente(businessId!, incidente.id)
  // Este GET auto-crea la investigación de la que cuelgan los comprobantes; `canOperate` evita
  // el 403 a quien no puede operar.
  const {data: inv} = useInvestigacion(businessId!, incidente.id, canOperate)
  const {data: evidencia} = useInvestigacionEvidencia(businessId!, incidente.id, canOperate)

  function comprobanteDe(kind: EvidenceKind) {
    if (!inv) return null
    return (
      <ComprobanteBloque
        businessId={businessId!}
        incidenteId={incidente.id}
        investigacionId={inv.id}
        kind={kind}
        items={(evidencia ?? []).filter((e) => e.kind === kind)}
      />
    )
  }

  const esAccidente = incidente.tipo === 'accidente'
  const esGraveMortal = incidente.gravedad_reportada === 'grave' || incidente.gravedad_reportada === 'mortal'
  const furatDone = incidente.furat_reported_at !== null
  const territorialDone = incidente.territorial_reported_at !== null
  const invStarted = incidente.investigacion_started_at !== null
  const cerrado = incidente.estado === 'cerrado'

  // El informe a la ARL (art. 14) va entre iniciar y cerrar, y el backend no exige su sello para
  // cerrar: por eso es un paso de la secuencia y no una excepción.
  const informeArlDone = !esGraveMortal || incidente.informe_arl_sent_at !== null

  // Mismo orden en que se pintan: el primero sin hacer es «lo siguiente».
  const pasos: boolean[] = [
    !esAccidente || furatDone,
    !esGraveMortal || territorialDone,
    invStarted,
    informeArlDone,
    cerrado,
  ]
  const siguienteIdx = pasos.findIndex((p) => !p)

  return (
    <div className="space-y-3" data-testid="incidente-checklist">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
          {t('incidentes.checklist.eyebrow')}
        </p>
        <PorQue
          label={t('incidentes.checklist.porqueLabel')}
          ariaLabel={t('incidentes.checklist.porqueAria', {paso: t('incidentes.checklist.eyebrow')})}
          titulo={t('incidentes.checklist.eyebrow')}
          testId="checklist-porque"
          contenidoTestId="checklist-porque-contenido">
          {/* No es `FichaDatos`: es el cuerpo de una ayuda, no datos del incidente. */}
          <dl className="space-y-2">
            {(
              [
                ['furatTitle', 'furatWhy'],
                ['territorialTitle', 'territorialWhy'],
                ['investigacionTitle', 'investigacionWhy'],
                ['informeArlTitle', 'informeArlWhy'],
              ] as const
            ).map(([titulo, porque]) => (
              <div key={titulo}>
                <dt className="text-xs font-semibold text-foreground">{t(`incidentes.checklist.${titulo}`)}</dt>
                <dd className="text-xs">{t(`incidentes.checklist.${porque}`)}</dd>
              </div>
            ))}
          </dl>
        </PorQue>
      </div>
      {esAccidente && (
        <ChecklistStep
          done={furatDone}
          siguiente={siguienteIdx === 0}
          title={t('incidentes.checklist.furatTitle')}
          comprobante={comprobanteDe('comprobante_arl_eps')}>
          <div className="space-y-2">
            <Button
              size="sm"
              variant={siguienteIdx === 0 ? 'default' : 'outline'}
              className="min-h-11 sm:min-h-0"
              data-testid="furat-generar"
              disabled={generar.isPending}
              onClick={() => generar.mutate(undefined)}>
              {t('incidentes.furat.generar')}
            </Button>
            <details>
              <summary className="flex min-h-11 cursor-pointer items-center text-xs font-medium text-primary sm:min-h-0">
                {t('incidentes.furat.yaLoRadique')}
              </summary>
              <div className="mt-2">
                <FuratManualForm businessId={businessId!} incidenteId={incidente.id} />
              </div>
            </details>
          </div>
        </ChecklistStep>
      )}

      {esGraveMortal && (
        <ChecklistStep
          done={territorialDone}
          siguiente={siguienteIdx === 1}
          title={t('incidentes.checklist.territorialTitle')}
          comprobante={comprobanteDe('comprobante_territorial')}>
          <Button
            size="sm"
            variant={siguienteIdx === 1 ? 'default' : 'outline'}
            className="min-h-11 sm:min-h-0"
            data-testid="territorial-marcar"
            disabled={territorial.isPending}
            onClick={() => territorial.mutate(undefined)}>
            {t('incidentes.checklist.territorialMarcar')}
          </Button>
        </ChecklistStep>
      )}

      <ChecklistStep
        done={invStarted}
        keepChildrenWhenDone
        siguiente={siguienteIdx === 2}
        title={t('incidentes.checklist.investigacionTitle')}>
        {invStarted ? (
          <Button asChild size="sm" variant="outline" className="min-h-11 sm:min-h-0" data-testid="investigacion-abrir">
            <Link to={`/businesses/${businessId}/incidentes/${incidente.id}/investigacion`}>
              {t('incidentes.checklist.investigacionAbrir')}
            </Link>
          </Button>
        ) : (
          <div className="space-y-2">
            <Button
              size="sm"
              variant={siguienteIdx === 2 ? 'default' : 'outline'}
              className="min-h-11 sm:min-h-0"
              data-testid="investigacion-iniciar"
              disabled={transicion.isPending}
              onClick={() => transicion.mutate({estado: 'en_investigacion'})}>
              {t('incidentes.checklist.investigacionIniciar')}
            </Button>
            <details>
              <summary
                data-testid="inicio-manual-toggle"
                className="flex min-h-11 cursor-pointer items-center text-xs font-medium text-primary sm:min-h-0">
                {t('incidentes.checklist.inicioManualToggle')}
              </summary>
              <div className="mt-2">
                <InicioManualForm
                  businessId={businessId!}
                  incidenteId={incidente.id}
                  eventoFecha={incidente.evento_fecha}
                />
              </div>
            </details>
          </div>
        )}
      </ChecklistStep>

      <InformeArlStep
        incidente={incidente}
        businessId={businessId!}
        siguiente={siguienteIdx === 3}
        comprobante={comprobanteDe('comprobante_informe_arl')}
      />

      <ChecklistStep done={cerrado} siguiente={siguienteIdx === 4} title={t('incidentes.checklist.cerrarTitle')}>
        <Button
          size="sm"
          variant={siguienteIdx === 4 ? 'default' : 'outline'}
          className="min-h-11 sm:min-h-0"
          data-testid="incidente-cerrar"
          disabled={!invStarted || transicion.isPending}
          onClick={() => transicion.mutate({estado: 'cerrado'})}>
          {t('incidentes.checklist.cerrar')}
        </Button>
        <Hint testId="incidente-cerrar-bloqueado">
          {t(invStarted ? 'incidentes.checklist.cerrarNota' : 'incidentes.checklist.cerrarBloqueado')}
        </Hint>
      </ChecklistStep>
    </div>
  )
}

/** Única vuelta atrás de un evento cerrado, con motivo escrito; la ruta y el backend exigen operador. */
export function ReabrirBox({incidente, businessId}: {incidente: Incidente; businessId: string}) {
  const {t} = useTranslation()
  const transicion = useTransicionarIncidente(businessId, incidente.id)
  const [reopenReason, setReopenReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  return (
    <SectionCard testId="incidente-reabrir" title={t('incidentes.reabrir.eyebrow')} bodyClassName="space-y-3 p-5">
      {incidente.reopened_at && (
        <p className="text-xs text-muted-foreground" data-testid="incidente-reabrir-historico">
          {t('incidentes.reabrir.historico', {
            fecha: formatDate(incidente.reopened_at),
            nombre: incidente.reopened_by_nombre ?? t('incidentes.reabrir.nombreDesconocido'),
            motivo: incidente.reopened_reason,
          })}
        </p>
      )}
      <Campo label={t('incidentes.reabrir.motivoLabel')} htmlFor="reabrir-motivo">
        <Input
          maxLength={tope('NOTA')}
          id="reabrir-motivo"
          type="text"
          value={reopenReason}
          data-testid="reabrir-motivo"
          onChange={(e) => setReopenReason(e.target.value)}
          placeholder={t('incidentes.reabrir.motivoPlaceholder')}
          className="h-11 sm:h-9"
        />
      </Campo>
      <Button
        size="sm"
        variant="outline"
        className="min-h-11 sm:min-h-0"
        data-testid="incidente-reabrir-submit"
        disabled={!reopenReason.trim() || transicion.isPending}
        onClick={() => {
          setError(null)
          transicion.mutate(
            {estado: 'en_investigacion', motivo: reopenReason.trim()},
            {onError: (err) => setError(translateApiError(err))}
          )
        }}>
        {t('incidentes.reabrir.submit')}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </SectionCard>
  )
}
